import { isDefined } from "@clipboard-health/util-ts";
import { Box, List } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { useEffect, useRef } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "./api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { AgentResultItem } from "./components/AgentResultItem";
import { ExpandedWorkerContent } from "./components/ExpandedWorkerContent";
import { FacilityResultItem } from "./components/FacilityResultItem";
import { LoadMoreItem } from "./components/LoadMoreItem";
import { SectionHeader } from "./components/SectionHeader";
import { type ShiftAction } from "./components/ShiftActionItem";
import { ShiftSection } from "./components/ShiftSection";
import { WorkplaceShiftItem } from "./components/WorkplaceShiftItem";
import { type SearchResultItem } from "./hooks/useOmnisearchResults";

export type CopiedItemType = ShiftAction | "shift" | "stripe" | "agent" | "facility";

interface OmnisearchResultsListProps {
  agents: Agent[];
  facilities: Facility[];
  shift: ShiftSearchResult | null;
  expandedShiftId: string | null;
  expandedWorkerId: string | null;
  expandedWorkplaceId: string | null;
  workerShifts: WorkerShift[];
  workerStripeAccount: WorkerStripeAccount | undefined;
  isLoadingWorkerShifts: boolean;
  isLoadingWorkerStripeAccount: boolean;
  workerHasShifts: boolean;
  workerHasStripeAccount: boolean;
  workerImpersonation: { canImpersonate: boolean; disabledReason?: string };
  workplaceShifts: WorkplaceShift[];
  isLoadingWorkplaceShifts: boolean;
  workplaceHasShifts: boolean;
  hasMoreAgents: boolean;
  isFetchingMoreAgents: boolean;
  hasMoreFacilities: boolean;
  isFetchingMoreFacilities: boolean;
  selectedIndex: number;
  copiedItem: CopiedItemType | null;
  onSelect: (item: SearchResultItem) => void;
}

export function OmnisearchResultsList(props: OmnisearchResultsListProps) {
  const {
    agents,
    facilities,
    shift,
    expandedShiftId,
    expandedWorkerId,
    expandedWorkplaceId,
    workerShifts,
    workerStripeAccount,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    workerImpersonation,
    workplaceShifts,
    isLoadingWorkplaceShifts,
    workplaceHasShifts,
    hasMoreAgents,
    isFetchingMoreAgents,
    hasMoreFacilities,
    isFetchingMoreFacilities,
    selectedIndex,
    copiedItem,
    onSelect,
  } = props;
  const isShiftExpanded = isDefined(shift) && expandedShiftId === shift.id;
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  useEffect(() => {
    const element = itemRefs.current.get(selectedIndex);
    element?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);
  const registerRef = (index: number) => (element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };

  const getExpandedWorkerItemCount = (): number =>
    workerShifts.length + (workerHasStripeAccount ? 1 : 0) + 1; // +1 for impersonate item

  const getAgentResultIndex = (agentIndex: number): number => {
    let index = 0;
    for (let agentNumber = 0; agentNumber < agentIndex; agentNumber++) {
      index +=
        1 + (expandedWorkerId === agents[agentNumber].userId ? getExpandedWorkerItemCount() : 0);
    }
    return index;
  };

  const showAgentsLoadMore = hasMoreAgents && agents.length > 0;
  const showFacilitiesLoadMore = hasMoreFacilities && facilities.length > 0;

  const agentsLoadMoreIndex =
    agents.length + (isDefined(expandedWorkerId) ? getExpandedWorkerItemCount() : 0);

  const getFacilitiesBaseIndex = (): number => agentsLoadMoreIndex + (showAgentsLoadMore ? 1 : 0);

  const getFacilityResultIndex = (facilityIndex: number): number => {
    let index = getFacilitiesBaseIndex();
    for (let facilityNumber = 0; facilityNumber < facilityIndex; facilityNumber++) {
      index +=
        1 +
        (expandedWorkplaceId === facilities[facilityNumber].userId ? workplaceShifts.length : 0);
    }
    return index;
  };

  const facilitiesLoadMoreIndex =
    getFacilitiesBaseIndex() +
    facilities.length +
    (isDefined(expandedWorkplaceId) ? workplaceShifts.length : 0);

  const shiftBaseIndex = facilitiesLoadMoreIndex + (showFacilitiesLoadMore ? 1 : 0);

  return (
    <List sx={{ py: 0 }}>
      {agents.length > 0 && (
        <>
          <SectionHeader label="Professionals" />
          {agents.map((agent, agentIndex) => {
            const isExpanded = expandedWorkerId === agent.userId;
            const resultIndex = getAgentResultIndex(agentIndex);
            return (
              <Box key={agent.userId}>
                <AgentResultItem
                  ref={registerRef(resultIndex)}
                  agent={agent}
                  copied={copiedItem === "agent" && selectedIndex === resultIndex}
                  expanded={isExpanded}
                  isLoadingShifts={isLoadingWorkerShifts || isLoadingWorkerStripeAccount}
                  selected={selectedIndex === resultIndex}
                  onClick={() => {
                    onSelect({ type: "agent", data: agent, expanded: isExpanded });
                  }}
                />
                {isExpanded && (
                  <ExpandedWorkerContent
                    agent={agent}
                    canImpersonateWorker={workerImpersonation.canImpersonate}
                    copiedItem={copiedItem}
                    impersonateDisabledReason={workerImpersonation.disabledReason}
                    isLoadingWorkerShifts={isLoadingWorkerShifts}
                    isLoadingWorkerStripeAccount={isLoadingWorkerStripeAccount}
                    registerRef={registerRef}
                    resultIndex={resultIndex}
                    selectedIndex={selectedIndex}
                    workerHasShifts={workerHasShifts}
                    workerHasStripeAccount={workerHasStripeAccount}
                    workerId={agent.userId}
                    workerShifts={workerShifts}
                    workerStripeAccount={workerStripeAccount}
                    onSelect={onSelect}
                  />
                )}
              </Box>
            );
          })}
          {showAgentsLoadMore && (
            <LoadMoreItem
              ref={registerRef(agentsLoadMoreIndex)}
              label="Load more workers"
              loading={isFetchingMoreAgents}
              selected={selectedIndex === agentsLoadMoreIndex}
              onClick={() => {
                onSelect({ type: "agent-load-more" });
              }}
            />
          )}
        </>
      )}
      {facilities.length > 0 && (
        <>
          <SectionHeader hasTopMargin={agents.length > 0} label="Workplaces" />
          {facilities.map((facility, facilityIndex) => {
            const isExpanded = expandedWorkplaceId === facility.userId;
            const resultIndex = getFacilityResultIndex(facilityIndex);
            return (
              <Box key={facility.userId}>
                <FacilityResultItem
                  ref={registerRef(resultIndex)}
                  copied={copiedItem === "facility" && selectedIndex === resultIndex}
                  expanded={isExpanded}
                  facility={facility}
                  isLoadingShifts={isLoadingWorkplaceShifts}
                  selected={selectedIndex === resultIndex}
                  onClick={() => {
                    onSelect({ type: "facility", data: facility, expanded: isExpanded });
                  }}
                />
                {isExpanded && !isLoadingWorkplaceShifts && workplaceHasShifts && (
                  <>
                    {workplaceShifts.map((workplaceShift, shiftIndex) => {
                      const shiftResultIndex = resultIndex + 1 + shiftIndex;
                      return (
                        <WorkplaceShiftItem
                          key={workplaceShift.id}
                          ref={registerRef(shiftResultIndex)}
                          copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                          selected={selectedIndex === shiftResultIndex}
                          shift={workplaceShift}
                          onClick={() => {
                            onSelect({
                              type: "workplace-shift",
                              data: workplaceShift,
                              workplaceId: facility.userId,
                            });
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isExpanded && !isLoadingWorkplaceShifts && !workplaceHasShifts && (
                  <Text color="text.secondary" sx={{ pl: 6, pr: 2, py: 1 }} variant="caption">
                    No recent or upcoming shifts
                  </Text>
                )}
              </Box>
            );
          })}
          {showFacilitiesLoadMore && (
            <LoadMoreItem
              ref={registerRef(facilitiesLoadMoreIndex)}
              label="Load more workplaces"
              loading={isFetchingMoreFacilities}
              selected={selectedIndex === facilitiesLoadMoreIndex}
              onClick={() => {
                onSelect({ type: "facility-load-more" });
              }}
            />
          )}
        </>
      )}
      {isDefined(shift) && (
        <ShiftSection
          copiedItem={copiedItem}
          hasTopMargin={agents.length > 0 || facilities.length > 0}
          isShiftExpanded={isShiftExpanded}
          registerRef={registerRef}
          selectedIndex={selectedIndex}
          shift={shift}
          shiftBaseIndex={shiftBaseIndex}
          onSelect={onSelect}
        />
      )}
    </List>
  );
}
